import { AgentType } from '@server/ai';
import { useSelector } from 'react-redux';
import { selectSearchText, selectIsGhostChat } from './agentsChatsReducer';
import useAgentsChats from './useAgentsChats';
import { useAgentChatInstance } from './AgentChatInstanceContext';
import { Stack } from '@mui/material';
import AgentsHomeCardsExpanded from './AgentsHomeCardsExpanded';
import AgentSideMenu from './agentSideMenu/AgentSideMenu';
import AgentChatEntryPoint from './AgentChatEntryPoint';
import { CHAT_AREA_MAX_WIDTH, FakeAgentType } from './aiAgentsConsts';
import ToggleExpandButton from './ToggleExpandButton';
import ClosePanelButton from './ClosePanelButton';
import AgentsHomeCardsCollapsed from './AgentsHomeCardsCollapsed';
import useAgentsHome from './useAgentsHome';
import AgentTopBar from './AgentTopBar';
import AiAssistantPage from '../pages/ai-assistant/AiAssistantPage';
import { selectActiveFakeAgentType, selectIsArtifactsViewActive } from './agentsHomeReducer';
import ArtifactsPage from '../pages/artifacts/ArtifactsPage';
import AgentsNotAvailable from './AgentsNotAvailable';
import useIsAnyAgentEnabled from './useIsAnyAgentEnabled';
import { AgentSettingsProvider } from './AgentSettingsContext';
import { ArtifactPanel } from '../common/artifacts/ArtifactPanel';
import { TableArtifactPanel } from '../common/artifacts/TableArtifactPanel';
import { useArtifacts } from '../common/artifacts/useArtifacts';
import { useTableArtifactsOptional } from '../common/artifacts/useTableArtifacts';
import { BuildDefinitionPanel } from '../common/build-definition/BuildDefinitionPanel';
import { useBuildDefinitionsOptional } from '../common/build-definition/useBuildDefinitions';
import { useArtifactPanelWidth } from '../../hooks/useArtifactPanelWidth';
import { IssuesPanel } from '../issues/IssuesPanel';
import { selectIsIssuesPanelOpen } from './agentsHomeReducer';
import { useIssuesPanelWidth } from '../../hooks/useIssuesPanelWidth';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { useParams } from 'react-router';

const AgentsHomeContent = ({
  closePanel,
  onNewAgentClick,
  isPageView,
}: {
  closePanel?: () => void;
  onNewAgentClick: (type: AgentType) => void;
  isPageView?: boolean;
}) => {
  const { agentId: agentIdFromUrl, chatId: chatIdFromUrl } = useParams<{
    agentId?: string;
    chatId?: string;
  }>();

  // Agent and chat state
  const { activeAgent, clearActiveAgentClickHandler } = useAgentsChats();
  const { chatInstanceId } = useAgentChatInstance();
  const isGhostChat = useSelector(selectIsGhostChat(chatInstanceId));

  // UI state
  const { isExpanded } = useAgentsHome();
  const isFullWidth = isExpanded || isPageView;
  const searchText = useSelector(selectSearchText);
  const activeFakeAgentType = useSelector(selectActiveFakeAgentType);
  const isArtifactsViewActive = useSelector(selectIsArtifactsViewActive);

  // Panel state
  const { isArtifactPanelOpen } = useArtifacts();
  const tableArtifactContext = useTableArtifactsOptional();
  const isTablePanelOpen = tableArtifactContext?.isTablePanelOpen ?? false;
  const buildDefinitionContext = useBuildDefinitionsOptional();
  const isBuildDefinitionPanelOpen = buildDefinitionContext?.isBuildDefinitionPanelOpen ?? false;
  const { panelWidth: artifactPanelWidth } = useArtifactPanelWidth();
  const isIssuesPanelOpen = useSelector(selectIsIssuesPanelOpen);
  const { panelWidth: issuesPanelWidth } = useIssuesPanelWidth();

  // Determine which side panel is open and its width
  const isSidePanelOpen =
    isArtifactPanelOpen || isTablePanelOpen || isBuildDefinitionPanelOpen || isIssuesPanelOpen;
  const panelWidth = isIssuesPanelOpen ? issuesPanelWidth : artifactPanelWidth;

  const isAnyAgentEnabled = useIsAnyAgentEnabled();

  if (!isAnyAgentEnabled) {
    return <AgentsNotAvailable />;
  }

  // Loading guards: Show loader while data is being fetched
  const isLoadingAgent = agentIdFromUrl && !activeAgent && !activeFakeAgentType;
  const isLoadingChat = chatIdFromUrl && isGhostChat;
  const shouldShowLoader = isLoadingAgent || isLoadingChat;

  // Rendering conditions
  const shouldShowAgentCards = !agentIdFromUrl && !activeAgent && !activeFakeAgentType;
  const shouldShowAgentWithChat = activeAgent && !shouldShowLoader;

  const panelButtons = (
    <Stack
      direction="row"
      gap={1}
      sx={
        isSidePanelOpen && !isPageView && isExpanded
          ? {
              position: 'absolute',
              right: '12px',
              top: '12px',
            }
          : {}
      }
    >
      <ToggleExpandButton />
      <ClosePanelButton closePanel={closePanel} />
    </Stack>
  );

  const renderFakeAgent = () => {
    if (!activeFakeAgentType) return null;
    switch (activeFakeAgentType) {
      case FakeAgentType.SupportAgent:
        return (
          <AiAssistantPage
            goBackCb={() => clearActiveAgentClickHandler()}
            isCollapsed={!isFullWidth}
            handleClosePanel={closePanel}
          />
        );

      default:
        return null;
    }
  };

  const renderAgentCards = () => (
    <>
      {!isFullWidth && (
        <AgentsHomeCardsCollapsed closePanel={closePanel} onNewAgentClick={onNewAgentClick} />
      )}
      {isFullWidth && (
        <AgentsHomeCardsExpanded
          closePanel={closePanel}
          onNewAgentClick={onNewAgentClick}
          isPageView={isPageView}
        />
      )}
    </>
  );

  const renderAgentWithChat = () => (
    <Stack
      position="relative"
      direction={isFullWidth ? 'row' : 'column'}
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {isFullWidth && <AgentSideMenu />}
      {!isFullWidth && <AgentTopBar closePanel={closePanel} />}

      <Stack
        flex={1}
        overflow="hidden"
        sx={{
          width: isSidePanelOpen ? `calc(100% - ${panelWidth}px)` : '100%',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {isArtifactsViewActive && activeAgent && <ArtifactsPage activeAgent={activeAgent} />}
        {!isArtifactsViewActive && (
          <AgentChatEntryPoint
            noteAlignment={isFullWidth ? 'center' : 'left'}
            maxWidth={CHAT_AREA_MAX_WIDTH}
            searchTermToHighlight={isFullWidth ? searchText : undefined}
            panelButtons={!isPageView && isFullWidth ? panelButtons : null}
          />
        )}
      </Stack>

      {isArtifactPanelOpen && <ArtifactPanel />}
      {isTablePanelOpen && <TableArtifactPanel />}
      {isBuildDefinitionPanelOpen && <BuildDefinitionPanel />}
      {isIssuesPanelOpen && <IssuesPanel />}
    </Stack>
  );

  return (
    <AgentSettingsProvider>
      <Stack
        height="100%"
        position="relative"
        sx={{
          flex: 1,
          alignItems: 'center',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {shouldShowLoader && <CenteredCircularProgress />}
        {shouldShowAgentCards && renderAgentCards()}
        {shouldShowAgentWithChat && renderAgentWithChat()}
        {renderFakeAgent()}
      </Stack>
    </AgentSettingsProvider>
  );
};

export default AgentsHomeContent;
